import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, subMonths, startOfWeek, endOfWeek, addDays } from 'date-fns';
import { da } from 'date-fns/locale';
import { FaChevronLeft, FaChevronRight, FaBars, FaProjectDiagram, FaTasks, FaUser } from 'react-icons/fa';
import CustomerWindow from '../crm/customerWindow';
import ProjectWindow from '../taskmanager/projects/projectWindow';
import './styles/monthly.css';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import TaskWindow from '../taskmanager/tasks/tasksWindow';

function MonthlyCalendar({ toggleMenu }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerWindow, setShowCustomerWindow] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    let unsubscribeCustomers;
    let unsubscribeProjects;
    let unsubscribeTasks;

    const setupListeners = () => {
      const visibleStart = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
      const visibleEnd = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });

      // Lyt til ændringer i customers collection
      const customersRef = collection(db, 'customers');
      const customerQuery = query(
        customersRef,
        where('accountOwner', '==', auth.currentUser.uid)
      );

      unsubscribeCustomers = onSnapshot(customerQuery, (snapshot) => {
        const docs = snapshot.docs;
        
        // Planlagte kontakter - nu med udvidet datointerval
        const leadEvents = docs
          .filter(doc => {
            const nextContact = doc.data().nextContact;
            const updates = doc.data().updates || [];
            
            // Skip hvis der ikke er nogen nextContact eller updates
            if (!nextContact && updates.length === 0) return false;
            
            // Find seneste opdatering
            const lastUpdate = updates.length > 0 
              ? new Date(Math.max(...updates.map(u => new Date(u.timestamp))))
              : null;
            
            // Hvis der er en nextContact dato
            if (nextContact) {
              const contactDate = new Date(nextContact);
              
              // Hvis der er en nyere opdatering end nextContact, vis ikke nextContact
              if (lastUpdate && lastUpdate > contactDate) {
                return false;
              }
              
              // Vis altid overskredet nextContact hvis der ikke er nyere opdateringer
              if (contactDate < new Date() && (!lastUpdate || lastUpdate <= contactDate)) {
                return true;
              }
              
              // Check om datoen er inden for den synlige periode
              return contactDate >= visibleStart && contactDate <= visibleEnd;
            }
            
            return false;
          })
          .map(doc => {
            const nextContact = new Date(doc.data().nextContact);
            const today = new Date();
            const updates = doc.data().updates || [];
            const lastUpdate = updates.length > 0 
              ? new Date(Math.max(...updates.map(u => new Date(u.timestamp))))
              : null;
            
            // Beregn dage til næste kontakt
            const daysUntilContact = Math.ceil((nextContact - today) / (1000 * 60 * 60 * 24));
            
            // Bestem status
            let status = 'normal';
            if (daysUntilContact < 0) {
              // Hvis der ikke er nogen opdatering efter den overskredet kontaktdato
              if (!lastUpdate || lastUpdate < nextContact) {
                status = 'overdue';
              }
            }
            
            return {
              id: doc.id,
              type: 'lead',
              date: nextContact,
              title: doc.data().name,
              status: status
            };
          });

        // Advarsels events baseret på sidste opdatering
        const warningEvents = docs
          .filter(doc => {
            // Skip hvis det er et vundet lead eller ikke interesseret
            if (['won', 'notInterested'].includes(doc.data().status)) return false;
            
            const updates = doc.data().updates || [];
            const nextContact = doc.data().nextContact;
            
            // Hvis der er en nextContact dato
            if (nextContact) {
              const contactDate = new Date(nextContact);
              const lastUpdate = updates.length > 0 
                ? new Date(Math.max(...updates.map(u => new Date(u.timestamp))))
                : null;
              
              // Vis warning hvis der er en opdatering efter nextContact
              return lastUpdate && lastUpdate > contactDate;
            }
            
            // Ellers, vis warning hvis der er updates men ingen planlagt kontakt
            return updates.length > 0;
          })
          .flatMap(doc => {
            const updates = doc.data().updates || [];
            const lastUpdate = new Date(Math.max(...updates.map(u => new Date(u.timestamp))));
            const now = new Date();
            
            // Beregn dage siden sidste opdatering
            const daysSinceUpdate = Math.floor((now - lastUpdate) / (1000 * 60 * 60 * 24));
            
            // Beregn datoer for advarsler
            const warningDate = new Date(lastUpdate);
            warningDate.setDate(warningDate.getDate() + 7);
            
            const criticalDate = new Date(lastUpdate);
            criticalDate.setDate(criticalDate.getDate() + 14);

            const events = [];

            // Hvis vi er under 14 dage siden sidste opdatering, vis warning
            if (daysSinceUpdate <= 14) {
              if (warningDate >= visibleStart && warningDate <= visibleEnd) {
                events.push({
                  id: doc.id,
                  type: 'warning',
                  date: warningDate,
                  title: doc.data().name,
                  status: doc.data().status,
                  daysSinceUpdate: 7
                });
              }
            }

            // Kun vis critical hvis warning er overskredet
            if (daysSinceUpdate > 14) {
              if (criticalDate >= visibleStart && criticalDate <= visibleEnd) {
                events.push({
                  id: doc.id,
                  type: 'critical',
                  date: criticalDate,
                  title: doc.data().name,
                  status: doc.data().status,
                  daysSinceUpdate: 14
                });
              }
            }

            return events;
          });

        // Lyt til ændringer i projects collection
        const projectsRef = collection(db, 'projects');
        unsubscribeProjects = onSnapshot(projectsRef, (projectsSnapshot) => {
          const projectEvents = projectsSnapshot.docs
            .filter(doc => {
              // Skip hvis projektet er færdigt eller ikke har deadline
              if (doc.data().status === 'done' || !doc.data().deadline) return false;
              
              const deadlineDate = new Date(doc.data().deadline);
              return deadlineDate >= visibleStart && deadlineDate <= visibleEnd;
            })
            .map(doc => {
              const deadline = new Date(doc.data().deadline);
              const today = new Date();
              const daysUntilDeadline = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
              
              let status = 'normal';
              if (daysUntilDeadline < 0) status = 'overdue';
              else if (daysUntilDeadline <= 3) status = 'warning';

              return {
                id: doc.id,
                type: 'project',
                date: deadline,
                title: doc.data().name,
                status: status,
                daysUntilDeadline
              };
            });

          // Lyt til ændringer i tasks collection
          const tasksRef = collection(db, 'tasks');
          const tasksQuery = query(
            tasksRef,
            where('responsible', '==', auth.currentUser.uid),
            where('completed', '==', false)
          );

          unsubscribeTasks = onSnapshot(tasksQuery, (tasksSnapshot) => {
            const taskEvents = tasksSnapshot.docs
              .filter(doc => {
                // Skip hvis opgaven er færdig eller ikke har deadline
                if (doc.data().completed || !doc.data().deadline) return false;
                
                const deadlineDate = new Date(doc.data().deadline);
                return deadlineDate >= visibleStart && deadlineDate <= visibleEnd;
              })
              .map(doc => {
                const deadline = new Date(doc.data().deadline);
                const today = new Date();
                const daysUntilDeadline = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
                
                let status = 'normal';
                if (daysUntilDeadline < 0) status = 'overdue';
                else if (daysUntilDeadline <= 3) status = 'warning';

                return {
                  id: doc.id,
                  type: 'task',
                  date: deadline,
                  title: doc.data().name,
                  status: status,
                  daysUntilDeadline,
                  projectId: doc.data().projectId
                };
              });

            // Opdater events state med alle events
            setEvents([...leadEvents, ...warningEvents, ...projectEvents, ...taskEvents]);
            setLoading(false);
          });
        });
      });
    };

    setupListeners();

    // Cleanup listeners når komponenten unmounts eller currentDate ændres
    return () => {
      if (unsubscribeCustomers) unsubscribeCustomers();
      if (unsubscribeProjects) unsubscribeProjects();
      if (unsubscribeTasks) unsubscribeTasks();
    };
  }, [currentDate]);

  const getDaysInMonth = () => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    
    // Find første mandag før eller på månedens start
    const firstDay = startOfWeek(start, { weekStartsOn: 1 });
    // Find sidste søndag efter eller på månedens slut
    const lastDay = endOfWeek(end, { weekStartsOn: 1 });

    // Generer array med alle dage
    return eachDayOfInterval({ start: firstDay, end: lastDay });
  };

  // Erstat den eksisterende daysInMonth variabel med getDaysInMonth funktionen
  const daysInMonth = getDaysInMonth();

  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  const getEventsForDay = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === date.getDate() &&
             eventDate.getMonth() === date.getMonth() &&
             eventDate.getFullYear() === date.getFullYear();
    });
  };

  const handleEventClick = async (event) => {
    try {
      switch (event.type) {
        case 'lead':
        case 'warning':
        case 'critical':
          const customerDoc = await getDoc(doc(db, 'customers', event.id));
          if (customerDoc.exists()) {
            setSelectedCustomer({
              id: customerDoc.id,
              ...customerDoc.data()
            });
            setShowCustomerWindow(true);
          }
          break;

        case 'project':
          const projectDoc = await getDoc(doc(db, 'projects', event.id));
          if (projectDoc.exists()) {
            setSelectedProject({
              id: projectDoc.id,
              ...projectDoc.data()
            });
          }
          break;

        case 'task':
          const taskDoc = await getDoc(doc(db, 'tasks', event.id));
          if (taskDoc.exists()) {
            setSelectedTask({
              id: taskDoc.id,
              ...taskDoc.data()
            });
          }
          break;
      }
    } catch (error) {
      console.error('Fejl ved håndtering af event klik:', error);
    }
  };

  const handleCloseCustomerWindow = () => {
    setShowCustomerWindow(false);
    setSelectedCustomer(null);
  };

  const handleCloseProjectWindow = () => {
    setSelectedProject(null);
  };

  const handleCloseTaskWindow = () => {
    setSelectedTask(null);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { draggableId, source, destination } = result;
    const [type, id] = draggableId.split('-');
    
    const newDate = new Date(destination.droppableId);
    newDate.setHours(12, 0, 0, 0);
    
    const formattedDate = format(newDate, 'yyyy-MM-dd');

    try {
      switch (type) {
        case 'project':
          const projectRef = doc(db, 'projects', id);
          await updateDoc(projectRef, {
            deadline: formattedDate,
            updatedAt: new Date().toISOString()
          });
          break;

        case 'task':
          const taskRef = doc(db, 'tasks', id);
          await updateDoc(taskRef, {
            deadline: formattedDate,
            updatedAt: new Date().toISOString()
          });
          break;

        case 'lead':
          const leadRef = doc(db, 'customers', id);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          // Basis opdatering uden updates array
          const updateData = {
            nextContact: formattedDate,
            updatedAt: new Date().toISOString()
          };

          // Tilføj kun en ny opdatering hvis datoen er i fremtiden
          if (newDate >= today) {
            updateData.updates = arrayUnion({
              type: 'contact_scheduled',
              timestamp: new Date().toISOString(),
              message: `Næste kontakt planlagt til ${format(newDate, 'd. MMMM yyyy', { locale: da })}`
            });
          }

          await updateDoc(leadRef, updateData);
          break;

        case 'warning':
        case 'critical':
          // Konverter warning/critical til en nextContact dato
          const customerRef = doc(db, 'customers', id);
          await updateDoc(customerRef, {
            nextContact: formattedDate,
            updatedAt: new Date().toISOString(),
            updates: arrayUnion({
              type: 'contact_scheduled',
              timestamp: new Date().toISOString(),
              message: `Næste kontakt planlagt til ${format(newDate, 'd. MMMM yyyy', { locale: da })} (Planlagt fra påmindelse)`
            })
          });
          break;
      }
    } catch (error) {
      console.error('Fejl ved opdatering af dato:', error);
    }
  };

  // Tilføj loading indikator
  if (loading) {
    return (
      <div className="calendar-container">
        <div className="calendar-header">
          <button className="menu-toggle" onClick={toggleMenu}>
            <FaBars />
          </button>
          <div className="calendar-navigation">
            <h2>Indlæser kalender...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="calendar-container">
        <div className="calendar-header">
          <button className="menu-toggle" onClick={toggleMenu}>
            <FaBars />
          </button>
          <div className="calendar-navigation">
            <button onClick={handlePrevMonth} className="nav-button">
              <FaChevronLeft />
            </button>
            <h2>{format(currentDate, 'MMMM yyyy', { locale: da })}</h2>
            <button onClick={handleNextMonth} className="nav-button">
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div className="calendar-grid">
          {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map(day => (
            <div key={day} className="calendar-day-header">{day}</div>
          ))}
          
          {daysInMonth.map(date => (
            <Droppable 
              droppableId={date.toISOString()} 
              key={date.toISOString()}
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`calendar-day ${
                    !isSameMonth(date, currentDate) ? 'other-month' : ''
                  } ${isToday(date) ? 'today' : ''} ${
                    snapshot.isDraggingOver ? 'dragging-over' : ''
                  }`}
                >
                  <div className="day-number">
                    {format(date, 'd')}
                  </div>
                  <div className="day-events">
                    {getEventsForDay(date).map((event, index) => (
                      event.type === 'project' || event.type === 'task' || event.type === 'lead' || 
                      event.type === 'warning' || event.type === 'critical' ? (
                        <Draggable
                          key={`${event.type}-${event.id}`}
                          draggableId={`${event.type}-${event.id}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`event ${event.type} ${event.status} ${
                                snapshot.isDragging ? 'dragging' : ''
                              }`}
                              title={`${event.title} - ${
                                event.type === 'lead' ? 
                                  'Træk for at ændre næste kontakt' :
                                event.type === 'warning' || event.type === 'critical' ?
                                  'Træk for at planlægge næste kontakt' :
                                  'Træk for at ændre deadline'
                              }`}
                              onClick={(e) => {
                                if (!snapshot.isDragging) {
                                  handleEventClick(event);
                                }
                              }}
                            >
                              <div className="event-content">
                                <span className="event-icon">
                                  {event.type === 'project' ? <FaProjectDiagram /> :
                                   event.type === 'task' ? <FaTasks /> :
                                   <FaUser />}
                                </span>
                                <span className="event-title">{event.title}</span>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ) : (
                        <div 
                          key={event.id}
                          className={`event ${event.type} ${event.status}`}
                          onClick={() => handleEventClick(event)}
                        >
                          <div className="event-content">
                            <span className="event-icon">
                              {event.type === 'project' ? <FaProjectDiagram /> :
                               event.type === 'task' ? <FaTasks /> :
                               <FaUser />}
                            </span>
                            <span className="event-title">{event.title}</span>
                          </div>
                        </div>
                      )
                    ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>

        {showCustomerWindow && selectedCustomer && (
          <CustomerWindow
            customer={selectedCustomer}
            onClose={handleCloseCustomerWindow}
            onUpdate={() => {
              // Opdateringen håndteres via fetchEvents når vinduet lukkes
            }}
          />
        )}

        {selectedProject && (
          <ProjectWindow
            project={selectedProject}
            onClose={handleCloseProjectWindow}
          />
        )}

        {selectedTask && (
          <TaskWindow
            task={selectedTask}
            onClose={handleCloseTaskWindow}
          />
        )}
      </div>
    </DragDropContext>
  );
}

export default MonthlyCalendar;
